import {
  collection,
  doc,
  getDoc,
  getDocs,
  limitToLast,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  Unsubscribe,
} from '@firebase/firestore';
import { db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { ChecklistThreadMessage } from '../types';
import type { PostChecklistMessageData } from '../types/api';
import { mapDoc, mapDocs } from '@utils/firestore';
import { AppError } from '../utils/errorHandler';
import { ChecklistThreadMessageSchema } from '../schemas/firestoreSchemas';

export type { PostChecklistMessageData } from '../types/api';

function threadDocId(structureId: string, itemId: string) {
  return `${structureId}_${itemId}`;
}

function messagesCollection(structureId: string, itemId: string) {
  return collection(
    db,
    COLLECTIONS.CHECKLIST_THREADS,
    threadDocId(structureId, itemId),
    COLLECTIONS.CHECKLIST_THREAD_MESSAGES
  );
}

// Bound thread reads to the most recent messages — these queries were
// unbounded, so a long-running thread would stream its entire history to
// every viewer. limitToLast keeps the newest N in ascending display order.
const MESSAGES_LIMIT = 100;

export const checklistThreadsApi = {
  async listMessages(structureId: string, itemId: string): Promise<ChecklistThreadMessage[]> {
    const q = query(
      messagesCollection(structureId, itemId),
      orderBy('createdAt', 'asc'),
      limitToLast(MESSAGES_LIMIT)
    );
    const snapshot = await getDocs(q);
    return mapDocs(snapshot, ChecklistThreadMessageSchema);
  },

  subscribeMessages(
    structureId: string,
    itemId: string,
    onChange: (messages: ChecklistThreadMessage[]) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    const q = query(
      messagesCollection(structureId, itemId),
      orderBy('createdAt', 'asc'),
      limitToLast(MESSAGES_LIMIT)
    );
    return onSnapshotWithRetry(
      q,
      (snapshot) => onChange(mapDocs(snapshot, ChecklistThreadMessageSchema)),
      (err) => onError?.(err as Error)
    );
  },

  async postMessage(
    structureId: string,
    itemId: string,
    data: PostChecklistMessageData
  ): Promise<ChecklistThreadMessage> {
    // Ensure the parent thread doc exists so listing threads (future work)
    // stays cheap; keep the doc body minimal.
    const threadRef = doc(db, COLLECTIONS.CHECKLIST_THREADS, threadDocId(structureId, itemId));
    await setDoc(threadRef, { itemId, structureId, updatedAt: serverTimestamp() }, { merge: true });

    const msgRef = doc(messagesCollection(structureId, itemId));
    const payload = {
      authorId: data.authorId,
      authorName: data.authorName,
      authorAvatarUrl: data.authorAvatarUrl ?? null,
      authorRole: data.authorRole,
      text: data.text.trim(),
      createdAt: serverTimestamp(),
    };
    await setDoc(msgRef, payload);

    const snap = await getDoc(msgRef);
    const created = mapDoc(snap, ChecklistThreadMessageSchema);
    if (!created) {
      throw new AppError('Thread message was created but could not be re-read.', 'FIRESTORE_ERROR');
    }
    return created;
  },

  async updateMessage(
    structureId: string,
    itemId: string,
    messageId: string,
    text: string
  ): Promise<void> {
    const msgRef = doc(messagesCollection(structureId, itemId), messageId);
    await updateDoc(msgRef, { text: text.trim(), updatedAt: serverTimestamp() });
  },

  async deleteMessage(structureId: string, itemId: string, messageId: string): Promise<void> {
    const msgRef = doc(messagesCollection(structureId, itemId), messageId);
    await deleteDoc(msgRef);
  },
};
